<script lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue';
import { LazyCollectionSelect, useCollection } from '#layers/data-model';

const __DsBlock = {
	type: 'container',
	icon: 'lucide:list',
	name: '$t:blocks.listing.name',
	description: '$t:blocks.listing.description',
	category: 'basic',
	events: [
		{
			name: 'open',
			payload: [
				{
					name: 'id',
					type: 'object',
					array: false,
					required: true,
				},
			],
		},
		{
			name: 'add',
			payload: [],
		},
		{
			name: 'emptyStateAction',
			payload: [],
		},
	],
	options: ({ form, context }) => form([
		{
			name: 'collectionId',
			render: LazyCollectionSelect,
			hidden: () => blockContextIsListingPage(context),
		},
		{
			name: 'title',
		},
		{
			name: 'description',
		},
		{
			name: 'detailPageOnOpen',
		},
		{
			name: 'showAddItem',
		},
		{
			name: 'detailPageOnAdd',
		},
		{
			name: 'canFilter',
		},
		{
			name: 'canEditDisplayOptions',
		},
		{
			name: 'selectionMode',
			type: 'enum',
			values: ['single', 'multiple'],
			block: BuiltinBlockIds.InputTileRadio,
			props: {
				items: [
					{
						value: 'single',
						label: '$t:blocks.listing.selection_modes.single',
						icon: 'lucide:square-check',
					},
					{
						value: 'multiple',
						label: '$t:blocks.listing.selection_modes.multiple',
						icon: 'lucide:list-checks',
					},
				],
			},
		},
		{
			name: 'selection',
			model: true,
		},
	]),

	slots: [
		{
			name: 'actions',
		},
	],

	locked: blockContextIsListingPage,

	hidden: blockContextIsContentPage,
}</script>

<script lang="ts" setup>
import type { CrudActionType } from '#layers/access';
import type { CompositePrimaryKey, GlobalCollectionId } from '#layers/data-model';
import type { ListingProps } from '~/components/Listing/Listing.vue';
import type { SelectionMode } from '~/composables/use-selection';
import type { LayoutEmptyState } from '~/types/layout';
import type { ListingView, SaveButtonType } from '~/types/listing';
import { reactivePick } from '@vueuse/core';
import { omit } from 'lodash-es';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { useI18n } from 'vue-i18n';
import { useRouter } from '#app/composables/router';
import { CrudAction } from '#layers/access';
import { blockRefKey, defineBlock, useBlockContext, useBlockInEditorContext } from '#layers/block-engine';
import { useTranslationUtils } from '#layers/i18n';
import { secondaryHeader } from '#layers/ui';
import { showConfirmationDialog } from '#layers/ui/utils/show-confirmation-dialog';
import { useListingPageContext } from '~/composables/block-context/use-listing-page-context';
import { useListingViews } from '~/composables/listing/use-listing-views';
import { useAuthorization } from '~/composables/use-authorization';
import { useCollectionRoutes } from '~/composables/use-collection-routes';
import { useDisplayNames } from '~/composables/use-display-names';
import { blockContextIsContentPage, blockContextIsListingPage } from '~/utils/block';
import { mergeAnd } from '~/utils/filters';
import { deserializeFilter, serializeFilter } from '~/utils/serialize-filter';

export type DefaultViewOptions = Pick<ListingView, 'layoutId' | 'displayOptions' | 'name' | 'icon'>;

interface ListingBlockProps extends Pick<ListingProps, 'canBulkDelete' | 'canEditDisplayOptions' | 'canFilter' | 'description' | 'title' | 'deleteLabels'> {
	/**
	 * The collection to fetch items from.
	 */
	collectionId?: GlobalCollectionId;

	selectionMode?: SelectionMode;

	/*
	* The default layout to use when a new view is created
	* default to LayoutTable
	*/
	defaultView?: DefaultViewOptions;
	/**
	 * Auto redirect to detail page on item open
	 */
	detailPageOnOpen?: boolean;
	/**
	 * Show the "Add Item" button
	 */
	showAddItem?: boolean;
	/**
	 * Auto redirect to detail page on item add
	 */
	detailPageOnAdd?: boolean;
	/**
	 * Custom empty state shown by the layout when there are no items and no active filters.
	 */
	emptyState?: LayoutEmptyState;
}

const props = withDefaults(defineProps<ListingBlockProps>(), {
	canFilter: true,
	canEditDisplayOptions: true,
	// Vue casts an absent Boolean prop to `false`; the explicit `undefined` default keeps "not passed"
	// distinct from an explicit `false`, so non-overriding listings fall through to `collectionCan`.
	canBulkDelete: undefined,
});

const emit = defineEmits<{
	open: [id: CompositePrimaryKey];
	add: [];
	emptyStateAction: [];
}>();

;

const forwardedListingProps = reactivePick(props, [
	'canEditDisplayOptions',
	'canFilter',
	'description',
	'deleteLabels',
]);

const selection = defineModel<CompositePrimaryKey[]>('selection', { default: () => [] });

const router = useRouter();
const { t } = useI18n();
const { translateObject } = useTranslationUtils();
const { blockInEditor, setProp } = useBlockInEditorContext();
const { emitOnRootBlock } = useBlockContext();
const listingPageContext = useListingPageContext();

const finalCollectionId = computed(() => props.collectionId || listingPageContext.collectionId || null);
const { collection } = useCollection(finalCollectionId);
const { detailRoute } = useCollectionRoutes(finalCollectionId);
/**
 * Is this listing block the default listing for this listing page (i.e. does the collection ID match the page defined collection ID)
 */
const isListingPageListing = computed(() => listingPageContext.isListingPage && listingPageContext.collectionId === finalCollectionId.value);

const {
	currentView,
	currentViewId,
	effectiveViews,
	addView,
	duplicateView,
	deleteCurrentView,
	defaultView: computedDefaultView,
	currentFilter,
	currentSearchTerm,
	currentSharedDisplayOptions,
	differsFromSharedView,
	differsFromSharedDisplay,
	saveForEveryone,
	resetToShared,
	getViewName,
} = useListingViews({
	collectionId: finalCollectionId,
	keyPrefix: listingPageContext.persistenceKeyPrefix,
	defaultViewOverride: computed(() => props.defaultView),
});

const saveButtonType = computed<SaveButtonType>(() => {
	if (!differsFromSharedView.value) return null;
	return 'dropdown';
});

const translatedProps = computed(() => translateObject(props, { keys: ['title', 'description'], pick: true }));
const { getCollectionDisplayName } = useDisplayNames();

const title = computed(
	() => translatedProps.value.title || (collection.value ? getCollectionDisplayName(collection.value) : undefined),
);

const filterForDisplay = computed({
	get: () => collection.value ? deserializeFilter(currentFilter.value, collection.value) : { _and: [] },
	set: (value) => {
		if (collection.value) {
			currentFilter.value = serializeFilter(value, collection.value);
		}
	},
});

const mergedFilter = computed(() => {
	const filter = listingPageContext.filter ?? { _and: [] };
	return mergeAnd(filterForDisplay.value, filter);
});

const isDisplayOptionsOpen = ref(false);

const { isRootChild } = useBlockContext();

const fullPageMode = computed(() => (isRootChild() && listingPageContext.pageIsFullWidth));
const headerTheme = computed(() => secondaryHeader());

const currentViewName = computed({
	get: () => currentView.value.name,
	set: (value) => {
		currentView.value = { ...currentView.value, name: value };
	},
});

const currentViewIcon = computed({
	get: () => currentView.value.icon,
	set: (value) => {
		currentView.value = { ...currentView.value, icon: value };
	},
});

const currentViewLayoutId = computed({
	get: () => currentView.value.layoutId,
	set: (value) => {
		currentView.value = { ...currentView.value, layoutId: value };
	},
});

const currentViewDisplayOptions = computed({
	get: () => currentView.value.displayOptions,
	set: (value) => {
		currentView.value = { ...currentView.value, displayOptions: value };
	},
});

const resolvedViewName = computed(() => getViewName(currentView.value));
const layoutDisplayName = computed(() => getViewName({ ...currentView.value, name: undefined }));

function handleSaveView(options?: { asCopy?: boolean }) {
	duplicateView({ asCopy: options?.asCopy, fromCurrentState: true });

	if (options?.asCopy) {
		isDisplayOptionsOpen.value = true;
	}
}

async function handleSaveForEveryone() {
	const confirmed = await showConfirmationDialog({
		props: {
			title: t('listing.views.save.save_for_everyone_confirm.title', { name: resolvedViewName.value }),
			description: t('listing.views.save.save_for_everyone_confirm.description'),
			confirm: { label: t('listing.views.save.save_for_everyone') },
		},
	});

	if (confirmed) {
		saveForEveryone();
	}
}

function resetFilters() {
	currentFilter.value = { _and: [] };
}

// Root element
// This blockRefKey can be used to manually reference the root element of the block in the editor
// although it's recommended to have a single static root display:block element.

const rootRef = useTemplateRef('rootRef');

defineExpose({
	[blockRefKey]: rootRef,
});

// Open item event

function onOpen(id: CompositePrimaryKey) {
	emit('open', id);

	if (isListingPageListing.value) {
		emitOnRootBlock('open', id);

		// Redirect to detail edit page
		if (props.detailPageOnOpen && collection.value) {
			const route = detailRoute(id);

			if (route) {
				router.push(route);
			}
		}
	}
}

// Add item

function onAddItem() {
	emit('add');

	if (isListingPageListing.value) {
		emitOnRootBlock('add');

		// Redirect to detail create page
		if (props.detailPageOnAdd && collection.value) {
			const route = detailRoute('+');

			if (route) {
				router.push(route);
			}
		}
	}
}

// Empty state action

function onEmptyStateAction() {
	// Content listing pages reuse the same create flow as the "Add Item" toolbar button.
	if (isListingPageListing.value) {
		onAddItem();
		return;
	}

	emit('emptyStateAction');
}

// Init selection

watch(() => listingPageContext.selection, (value) => {
	if (value) {
		selection.value = value;
	}
}, {
	immediate: true,
});

watch(selection, (value) => {
	emitOnRootBlock('update:selection', value);
});

function handleUpdateDisplayOptions(displayOptions: Record<string, unknown> | undefined) {
	currentViewDisplayOptions.value = displayOptions;
}

const selectionMode = computed(() => props.selectionMode ?? listingPageContext.selectionMode);

const { can } = useAuthorization();

function collectionCan(action: CrudActionType, fallback: boolean): boolean {
	const resolvedCollection = collection.value;
	return resolvedCollection ? can({ collection: resolvedCollection, action }) : fallback;
}

// Collection-level delete (no id): for a covered scoped-delete collection this resolves to
// "can delete at least one"; the backend enforces per-id on bulk execution. A caller may pass
// `canBulkDelete` to gate on a dedicated entitlement (e.g. project members / service accounts).
const resolvedCanBulkDelete = computed(() => props.canBulkDelete ?? collectionCan(CrudAction.Delete, false));
const canRead = computed(() => collectionCan(CrudAction.Read, true));
const canCreate = computed(() => collectionCan(CrudAction.Create, false));
const canAddItem = computed(() => props.showAddItem && canCreate.value);

const translatedEmptyState = computed(() =>
	translateObject(props.emptyState, { keys: ['description', 'actionLabel'] }),
);

const resolvedEmptyState = computed(() => {
	const state = translatedEmptyState.value;
	if (!state) return;

	// The listing-page empty-state CTA creates an item; hide it when the user can't create one.
	if (isListingPageListing.value && !canAddItem.value) {
		return omit(state, ['actionLabel']);
	}

	return state;
});
</script>

<template>
	<div
		v-if="!finalCollectionId"
		ref="rootRef"
		class="p-4 font-medium"
	>
		<template v-if="blockInEditor">
			{{ $t('blocks.listing.select_collection') }}
		</template>
		<template v-else>
			{{ $t('blocks.listing.missing_collection') }}
		</template>
	</div>

	<DEmpty
		v-else-if="!collection"
		ref="rootRef"
		:title="$t('blocks.listing.collection_not_found')"
		:description="$t('blocks.listing.collection_not_found_description')"
	/>

	<DEmpty
		v-else-if="!canRead"
		ref="rootRef"
		:title="$t('permissions.no_read_access_title')"
		:description="$t('permissions.no_read_access')"
	>
		<template v-if="canAddItem" #action>
			<div class="w-full">
				<ButtonCreateItem
					:collection-id="finalCollectionId"
					@click="onAddItem"
				/>
			</div>
		</template>
	</DEmpty>

	<Listing
		v-else
		ref="rootRef"
		v-bind="forwardedListingProps"
		v-model:filter="filterForDisplay"
		v-model:search-term="currentSearchTerm"
		v-model:display-options="currentViewDisplayOptions"
		v-model:layout-id="currentViewLayoutId"
		v-model:view-name="currentViewName"
		v-model:view-icon="currentViewIcon"
		v-model:display-options-open="isDisplayOptionsOpen"
		v-model:selection="selection"
		:view-id="currentViewId"
		:display-has-changes="differsFromSharedDisplay"
		:shared-display-options="currentSharedDisplayOptions"
		:layout-display-name
		:collection-id="finalCollectionId"
		:title
		:show-selection-actions="!listingPageContext.pageDisplayMode"
		:class="{
			'rounded-none! border-none!': fullPageMode,
		}"
		:can-bulk-delete="resolvedCanBulkDelete"
		@save-for-everyone="handleSaveForEveryone"
		@reset-view="resetToShared"
	>
		<template #title>
			<DInput
				v-if="blockInEditor"
				:model-value="title"
				:placeholder="$t('blocks.listing.new_listing')"
				:ui="{ base: ['rounded-none py-0 ps-0', headerTheme.title()] }"
				variant="none"
				:autofocus="!title"
				@update:model-value="setProp('title', $event)"
			/>
			<template v-else>
				{{ title }}
			</template>
		</template>
		<template #views>
			<ListingViews
				v-model:current-view-id="currentViewId"
				:views="effectiveViews"
				:default-view="computedDefaultView"
				@add="(id) => {
					addView(id);
					isDisplayOptionsOpen = true;
				}"
				@edit="() => {
					isDisplayOptionsOpen = true;
				}"
				@duplicate="() => {
					duplicateView();
					isDisplayOptionsOpen = true;
				}"
				@delete-view="deleteCurrentView"
			/>
		</template>
		<template #default="{ layoutProps, searchTerm }">
			<BlockRender
				v-if="currentView && layoutProps.collection"
				:key="`${finalCollectionId}-${currentView.layoutId}`"
				:block="currentView.layoutId"
				:props="{
					...layoutProps,
					selectionMode,
					searchTerm,
					onOpen,
					'emptyState': resolvedEmptyState,
					'filter': mergedFilter,
					'selection': selection,
					'onUpdate:displayOptions': handleUpdateDisplayOptions,
					'onUpdate:selection': (value: CompositePrimaryKey[]) => selection = value,
					'onResetFilters': resetFilters,
					'onEmptyStateAction': onEmptyStateAction,
				}"
			>
				<template #actions>
					<BlockMarkHorizontalLayout>
						<slot name="actions" />
					</BlockMarkHorizontalLayout>
				</template>
			</BlockRender>
		</template>
		<template #filter-actions>
			<ListingSaveActions
				:save-button-type
				@save="handleSaveView"
				@save-to-view="handleSaveForEveryone"
				@save-as-new="handleSaveView({ asCopy: true })"
				@reset="resetToShared"
			/>
		</template>
		<template #actions>
			<BlockMarkHorizontalLayout>
				<slot name="actions" />
			</BlockMarkHorizontalLayout>

			<ButtonCreateItem v-if="showAddItem" :collection-id="finalCollectionId" @click="onAddItem" />
		</template>
	</Listing>
</template>
